import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';

export interface ConfirmToggleActiveDialogProps {
  open: boolean;
  roleName: string;
  /** true = role is currently inactive and this confirms re-activating it. */
  activating: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** True while the activate/deactivate PATCH request is in flight. */
  isPending?: boolean;
}

/**
 * Confirm-before-toggle dialog for deactivating/activating a role. Mirrors
 * the `ModalDialog` header/footer shape used by
 * `admin/format-groups/components/deactivate-group-confirm.tsx`, kept
 * intentionally neutral (no amber/destructive icon) per an earlier client
 * feedback round that simplified the palette down to a single accent color.
 */
export function ConfirmToggleActiveDialog({
  open,
  roleName,
  activating,
  onCancel,
  onConfirm,
  isPending = false,
}: ConfirmToggleActiveDialogProps) {
  const { t } = useTranslation('admin');

  if (!open) return null;

  return (
    <ModalDialog
      header={
        <h2 className="text-[15px] font-semibold">
          {activating
            ? t('permissionMatrix.confirmToggle.activateTitle')
            : t('permissionMatrix.confirmToggle.deactivateTitle')}
        </h2>
      }
      onClose={onCancel}
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            {t('permissionMatrix.confirmToggle.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={isPending} data-testid="confirm-toggle-active-btn">
            {activating
              ? t('permissionMatrix.confirmToggle.activateConfirm')
              : t('permissionMatrix.confirmToggle.deactivateConfirm')}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-foreground/90 leading-relaxed">
        {activating
          ? t('permissionMatrix.confirmToggle.activateBody', { name: roleName })
          : t('permissionMatrix.confirmToggle.deactivateBody', { name: roleName })}
      </p>
    </ModalDialog>
  );
}
